import { Container, Grid, Typography, Button, IconButton, Chip, Dialog, DialogTitle, DialogContent, Tooltip } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import globals from "../globals";
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('bg-BG', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const WebinarsPage = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [webinars, setWebinars] = useState([]);
    const [registrationsFor, setRegistrationsFor] = useState(null); // webinar object
    const [registrations, setRegistrations] = useState([]);
    const [registrationsLoading, setRegistrationsLoading] = useState(false);

    const fetchWebinars = useCallback(() => {
        axios.get(`${globals.apiUrl}/webinars/?include_unpublished=1`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            setWebinars(res.data || []);
        }).catch((err) => {
            console.error('Failed to fetch webinars', err);
        });
    }, [token]);

    useEffect(() => {
        fetchWebinars();
    }, [fetchWebinars]);

    const openRegistrations = (webinar) => {
        setRegistrationsFor(webinar);
        setRegistrationsLoading(true);
        setRegistrations([]);
        axios.get(`${globals.apiUrl}/webinars/${webinar.id}/registrations`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            setRegistrations(res.data || []);
            setRegistrationsLoading(false);
        }).catch((err) => {
            console.error('Failed to fetch registrations', err);
            setRegistrationsLoading(false);
        });
    };

    const togglePublished = (webinar) => {
        const formData = new FormData();
        formData.append('data', JSON.stringify({ is_published: !webinar.is_published }));
        axios.put(`${globals.apiUrl}/webinars/${webinar.id}`, formData, {
            headers: { Authorization: `Bearer ${token}` },
        }).then(() => {
            fetchWebinars();
        }).catch((err) => {
            console.error('Failed to change webinar status', err);
        });
    };

    const handleDelete = (id) => {
        if (!window.confirm('Сигурни ли сте, че искате да изтриете този уебинар?')) return;
        axios.delete(`${globals.apiUrl}/webinars/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then(() => {
            fetchWebinars();
        }).catch((err) => {
            console.error('Failed to delete webinar', err);
        });
    };

    return (
        <Container>
            <Grid container justifyContent={'space-between'} alignItems={'center'} sx={{ mb: 3 }}>
                <Grid item>
                    <Typography sx={{ fontSize: 24, fontWeight: 'bold', color: globals.colors.darkBlue }}>
                        Уебинари
                    </Typography>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/admin-edit-webinar')}
                        sx={{ backgroundColor: globals.colors.lightRed, textTransform: 'none', '&:hover': { backgroundColor: globals.colors.darkRed } }}
                    >
                        Създай уебинар
                    </Button>
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table size="small" aria-label="webinars table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Заглавие</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Параметър</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Дата</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="center">Статус</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Действия</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {webinars.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ color: globals.colors.gray }}>
                                    Няма създадени уебинари.
                                </TableCell>
                            </TableRow>
                        )}
                        {webinars.map((webinar) => (
                            <TableRow key={webinar.id}>
                                <TableCell sx={{ fontWeight: 'bold', color: globals.colors.lightRed }}>
                                    {webinar.title || '(без заглавие)'}
                                </TableCell>
                                <TableCell>{webinar.slug || '—'}</TableCell>
                                <TableCell>{formatDate(webinar.date)}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title={webinar.is_published ? 'Върни в чернова' : 'Публикувай'}>
                                        <Chip
                                            label={webinar.is_published ? 'Публикуван' : 'Чернова'}
                                            color={webinar.is_published ? 'success' : 'default'}
                                            size="small"
                                            onClick={() => togglePublished(webinar)}
                                        />
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Записани">
                                        <IconButton
                                            aria-label="registrations"
                                            onClick={() => openRegistrations(webinar)}
                                            sx={{ color: globals.colors.lightGreen }}
                                        >
                                            <PeopleIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <IconButton
                                        aria-label="edit"
                                        onClick={() => navigate(`/admin-edit-webinar/${webinar.id}`)}
                                        sx={{ color: globals.colors.darkBlue }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        aria-label="delete"
                                        onClick={() => handleDelete(webinar.id)}
                                        sx={{ color: globals.colors.darkRed }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={Boolean(registrationsFor)} onClose={() => setRegistrationsFor(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold' }}>
                    <span>Записани — {registrationsFor?.title || ''}</span>
                    <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                            label={`Общо: ${registrations.length}`}
                            size="small"
                            sx={{ mr: 1, backgroundColor: globals.colors.darkBlue, color: 'white' }}
                        />
                        <IconButton onClick={() => setRegistrationsFor(null)}><CloseIcon /></IconButton>
                    </Grid>
                </DialogTitle>
                <DialogContent dividers>
                    {registrationsLoading ? (
                        <Typography sx={{ color: globals.colors.gray }}>Зареждане...</Typography>
                    ) : registrations.length === 0 ? (
                        <Typography sx={{ color: globals.colors.gray }}>Все още няма записани.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Име</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Имейл</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Платено</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {registrations.map((r) => (
                                    <TableRow key={`${r.user_id}-${r.created_at}`}>
                                        <TableCell>{r.name || '—'}</TableCell>
                                        <TableCell>{r.email || '—'}</TableCell>
                                        <TableCell align="right">
                                            {r.is_free ? 'Безплатно' : `€${((r.amount_paid || 0) / 100).toFixed(2)}`}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>
        </Container>
    );
};

export default WebinarsPage;
